import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useTimer } from "../../context/TimerContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { 
  Clock, 
  CheckCircle2, 
  ChevronRight,
  Trophy,
  Flame,
  Calendar,
  History,
  Lightbulb,
  Settings,
  RefreshCw,
  Zap,
  Star,
  Pause,
  Play,
  X,
  Timer,
  PlayCircle,
  Dumbbell,
  TrendingUp,
  TrendingDown,
  Info,
  Lock,
  Target
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import LevelSelectorPage from "./LevelSelectorPage";
import ExerciseTrackingModal from "../../components/ExerciseTrackingModal";
import WeeklyStatsWidget from "../../components/WeeklyStatsWidget";
import ExerciseDetailModal from "../../components/ExerciseDetailModal";
import SupersetBlock from "../../components/SupersetBlock";
import WorkoutMode from "./WorkoutMode";
import WorkoutPreview from "./WorkoutPreview";
import { StreakWidget, useStreak } from "../../components/StreakSystem";
import { PremiumBanner } from "../../components/PremiumLock";
import PremiumPopup, { usePremiumPopup } from "../../components/PremiumPopup";
import OnboardingFlow from "../../components/OnboardingFlow";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/ycyh3qv2_goatgym_logo.png.jpeg";

const motivationalLines = [
  "Cada repetición cuenta.",
  "Hoy es tu día.",
  "Sin excusas.",
  "Supera tus límites.",
  "El esfuerzo vale la pena.",
];

// Helper to get technique display info
const getTechniqueDisplay = (tech) => {
  const techniques = {
    myo_reps: { icon: "⚡", color: "#F59E0B", name: "Myo-reps" },
    dropset: { icon: "📉", color: "#EF4444", name: "Drop-set" },
    isometric: { icon: "⏱️", color: "#3B82F6", name: "Isométrico" },
    topset: { icon: "🎯", color: "#8B5CF6", name: "Top set" },
    deadstop: { icon: "⏸️", color: "#EC4899", name: "Dead-stop" },
    partials: { icon: "↕️", color: "#14B8A6", name: "Parciales" },
    negatives: { icon: "➖", color: "#F97316", name: "Negativas" },
    zigzag: { icon: "🔄", color: "#06B6D4", name: "Zig-zag" },
    rm: { icon: "🏆", color: "#D4AF37", name: "RM" },
  };
  
  const techDef = techniques[tech.type];
  if (!techDef) return null;
  
  let label = techDef.name;
  const configValue = tech.config?.value;
  
  // Add config info to label
  if (configValue) {
    switch (tech.type) {
      case "dropset":
        label = `Drop-set (${configValue} drops)`;
        break;
      case "isometric":
        label = `Isométrico ${configValue}s`;
        break;
      case "rm":
        label = `${configValue}RM`;
        break;
      case "myo_reps":
        label = `Myo-reps (${configValue})`;
        break;
      case "topset":
        label = `Top set (${configValue})`;
        break;
      default:
        break;
    }
  }
  
  return { ...techDef, label };
};

// Splash Screen Component
const SplashScreen = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 1800);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 bg-[#0B0B0B] z-50 flex flex-col items-center justify-center">
      <div className="animate-fade-in-up">
        <img 
          src={LOGO_URL} 
          alt="GOATGYM" 
          className="w-40 h-40 object-contain mb-6"
        />
        <p className="text-center text-[#D4AF37] font-heading font-bold text-lg tracking-wide">
          GOATGYM
        </p>
        <p className="text-center text-[#666] text-sm mt-1">
          Greatest Of All Time
        </p>
      </div>
    </div>
  );
};

// Motivational messages based on streak
const getStreakMessage = (streak) => {
  if (streak >= 10) return "Nivel élite. Sigue imparable.";
  if (streak >= 6) return "No rompas la racha.";
  if (streak >= 3) return "Ya estás creando disciplina.";
  if (streak >= 1) return "Buen inicio. Sigue así.";
  return "Empieza hoy tu racha.";
};

// Dynamic CTA text based on streak
const getCtaText = (streak, trainedToday) => {
  if (trainedToday) return "Sesión completada";
  if (streak >= 3) return "No rompas la racha";
  if (streak >= 1) return "Suma un día más";
  return "Ver entreno";
};

// GOATGYM motivational quotes
const goatQuotes = [
  "Aquí no vienes a probar. Vienes a mejorar.",
  "Disciplina diaria.",
  "Mentalidad GOAT.",
  "La constancia vence al talento.",
  "Hoy es el día.",
  "Cada rep cuenta.",
  "Sin excusas."
];

// Enhanced Streak Badge Component
const StreakBadge = ({ streak, bestStreak, showMessage = false, size = "normal", className = "" }) => {
  if (!streak && streak !== 0) return null;
  
  const isLarge = size === "large";
  const message = getStreakMessage(streak);
  
  return (
    <div className={cn("flex flex-col", className)}>
      <div className={cn(
        "flex items-center gap-2 bg-gradient-to-r from-orange-500/20 via-red-500/15 to-yellow-500/20 border border-orange-500/40 rounded-2xl transition-all",
        isLarge ? "px-5 py-3" : "px-3 py-1.5 rounded-full"
      )}>
        <div className={cn(
          "flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-red-500",
          isLarge ? "w-10 h-10" : "w-6 h-6"
        )}>
          <Flame className={cn(
            "text-white",
            isLarge ? "w-5 h-5" : "w-3.5 h-3.5"
          )} />
        </div>
        <div className="flex flex-col">
          <div className="flex items-baseline gap-1.5">
            <span className={cn(
              "font-heading font-black text-white",
              isLarge ? "text-3xl" : "text-lg"
            )}>
              {streak}
            </span>
            <span className={cn(
              "text-[#888] font-medium",
              isLarge ? "text-base" : "text-xs"
            )}>
              {streak === 1 ? "día" : "días"}
            </span>
          </div>
          {isLarge && showMessage && (
            <span className="text-orange-400/80 text-xs font-medium">{message}</span>
          )}
        </div>
        {!isLarge && bestStreak > 0 && bestStreak > streak && (
          <div className="flex items-center gap-1 text-[#555] text-xs ml-1 pl-2 border-l border-orange-500/20">
            <Trophy className="w-3 h-3" />
            <span>{bestStreak}</span>
          </div>
        )}
      </div>
      {isLarge && bestStreak > 0 && bestStreak > streak && (
        <div className="flex items-center justify-center gap-1 text-[#555] text-xs mt-2">
          <Trophy className="w-3 h-3" />
          <span>Tu mejor racha: {bestStreak} días</span>
        </div>
      )}
    </div>
  );
};

// Enhanced Celebration Overlay Component
const CelebrationOverlay = ({ streak, bestStreak, onClose }) => {
  const [showContent, setShowContent] = useState(false);
  const isNewRecord = streak > 0 && streak >= (bestStreak || 0);
  
  useEffect(() => {
    // Stagger animation
    setTimeout(() => setShowContent(true), 200);
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const celebrationMessages = [
    "Disciplina > motivación",
    "Hoy has hecho lo que otros no",
    "Un día más cerca de tu mejor versión",
    "La constancia es tu superpoder"
  ];
  
  const randomMessage = celebrationMessages[Math.floor(Math.random() * celebrationMessages.length)];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B0B0B]/98 animate-fade-in pt-safe pb-safe"
      onClick={onClose}
    >
      <div className={cn(
        "text-center px-8 max-w-sm transition-all duration-500",
        showContent ? "opacity-100 scale-100" : "opacity-0 scale-95"
      )}>
        {/* Celebration icon with particles */}
        <div className="relative mb-8">
          <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#D4AF37] via-[#F59E0B] to-orange-500 flex items-center justify-center mx-auto shadow-2xl shadow-[#D4AF37]/40 animate-pulse">
            <Flame className="w-14 h-14 text-white" />
          </div>
          {/* Animated particles */}
          <Star className="absolute -top-3 -right-1 w-7 h-7 text-[#D4AF37] animate-bounce" style={{ animationDuration: '1s' }} />
          <Star className="absolute -bottom-2 -left-4 w-5 h-5 text-[#F59E0B] animate-bounce" style={{ animationDuration: '1.2s', animationDelay: '0.2s' }} />
          <Zap className="absolute top-2 -left-6 w-6 h-6 text-orange-400 animate-bounce" style={{ animationDuration: '0.8s', animationDelay: '0.4s' }} />
          <Trophy className="absolute -top-1 left-0 w-5 h-5 text-yellow-400 animate-bounce" style={{ animationDuration: '1.1s', animationDelay: '0.3s' }} />
          {isNewRecord && (
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0B0B0B] text-xs font-bold px-3 py-1 rounded-full animate-bounce">
              ¡NUEVO RÉCORD!
            </div>
          )}
        </div>
        
        {/* Main text */}
        <div className="space-y-4 mb-6">
          <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-2xl px-6 py-4 inline-block">
            <p className="text-orange-400 text-sm font-semibold mb-1">+1 día añadido</p>
            <div className="flex items-center justify-center gap-2">
              <Flame className="w-7 h-7 text-orange-500" />
              <span className="text-4xl font-heading font-black text-white">{streak}</span>
              <span className="text-[#888] text-lg">
                {streak === 1 ? "día" : "días"}
              </span>
            </div>
          </div>
          
          <p className="text-white text-xl font-heading font-bold">
            {streak >= 10 ? "¡Imparable!" :
             streak >= 7 ? "¡Máquina!" :
             streak >= 3 ? "¡En racha!" :
             "¡Completado!"}
          </p>
        </div>
        
        {/* Motivational quote */}
        <p className="text-[#D4AF37] text-sm font-medium italic mb-6">
          "{randomMessage}"
        </p>
        
        {/* CTA */}
        <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-6 py-4">
          <p className="text-[#888] text-sm mb-1">Tu próximo objetivo:</p>
          <p className="text-white font-bold">Volver mañana y mantener la racha</p>
        </div>
        
        <p className="text-[#444] text-xs mt-6">Toca para continuar</p>
      </div>
    </div>
  );
};

// Rest Timer motivational phrases
const restMotivationalPhrases = [
  "Respira. Recarga. Repite.",
  "El descanso es parte del entreno.",
  "Prepárate para la siguiente serie.",
  "Cada segundo cuenta.",
  "Recupera fuerzas.",
  "Mantén el foco.",
  "La constancia gana."
];

// Get last workout relative date
const getLastWorkoutText = (lastWorkoutDate) => {
  if (!lastWorkoutDate) return "Sin entrenos aún";
  const last = new Date(lastWorkoutDate);
  const today = new Date();
  const diffDays = Math.floor((today - last) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return "Hoy";
  if (diffDays === 1) return "Ayer";
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return last.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
};

// Welcome Screen Component
const WelcomeScreen = ({ workout, program, profile, streak, trainedToday, onStartWorkout, onViewDetails, onNavigate, onChangeLevel, hasCustomProgram }) => {
  const [randomQuote] = useState(goatQuotes[Math.floor(Math.random() * goatQuotes.length)]);
  
  // Check if this is a custom program workout
  const isCustomWorkout = workout?.is_custom_program || hasCustomProgram;
  
  // Dynamic day names based on program
  const getDayName = (code) => {
    // For custom workouts, use the day_name directly
    if (isCustomWorkout && workout?.day_name) {
      return workout.day_name;
    }
    // Usar el nombre del workout si está disponible
    if (workout?.name) {
      return workout.name;
    }
    // Fallback a weekly_structure
    if (program?.weekly_structure) {
      const parts = program.weekly_structure.split(" / ");
      const codeIndex = ["A", "B", "C", "D", "E", "F"].indexOf(code);
      if (codeIndex >= 0 && codeIndex < parts.length) {
        return parts[codeIndex];
      }
    }
    return `Día ${code}`;
  };

  // Get level display name - with fallback
  const getLevelName = (level) => {
    if (!level) return "ACTIVO"; // Fallback when level is not set
    const names = {
      principiante: "PRINCIPIANTE",
      intermedio: "INTERMEDIO",
      avanzado: "AVANZADO",
      goat: "GOAT"
    };
    return names[level] || level?.toUpperCase() || "ACTIVO";
  };

  // Dynamic CTA text
  const ctaText = getCtaText(streak?.current_streak || 0, trainedToday);
  const ctaDisabled = trainedToday;

  return (
    <div className="min-h-[85vh] flex flex-col animate-fade-in px-0 sm:px-1 w-full max-w-full overflow-x-hidden" data-testid="welcome-screen">
      {/* Header with Change Level */}
      <div className="flex items-center justify-end pt-2 pb-2">
        <button
          onClick={onChangeLevel}
          className="flex items-center gap-1.5 text-[#888] hover:text-[#D4AF37] transition-colors text-xs sm:text-sm px-2 sm:px-3 py-1.5 rounded-lg hover:bg-[#1A1A1A]"
          data-testid="change-level-btn"
        >
          <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          Cambiar Nivel
        </button>
      </div>

      {/* Main Streak Display - PROMINENT */}
      <div className="text-center mb-4 sm:mb-6">
        {streak?.current_streak > 0 ? (
          <div className="inline-block">
            <StreakBadge 
              streak={streak.current_streak} 
              bestStreak={streak.best_streak}
              size="large"
              showMessage={true}
              className="justify-center"
            />
          </div>
        ) : (
          <div className="inline-flex flex-col items-center bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl px-4 sm:px-6 py-3 sm:py-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#2A2A2A] flex items-center justify-center mb-2">
              <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-[#555]" />
            </div>
            <p className="text-[#888] text-xs sm:text-sm font-medium">Empieza tu racha hoy</p>
          </div>
        )}
      </div>

      {/* GOATGYM Header with Quote */}
      <div className="text-center pb-3 sm:pb-4">
        <h1 className="text-2xl sm:text-3xl font-heading font-black text-white mb-1 sm:mb-2">
          GOATGYM
        </h1>
        <p className="text-[#D4AF37] text-xs sm:text-sm font-medium italic px-4">
          "{randomQuote}"
        </p>
      </div>

      {/* Info Block - Level, Last Workout */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-5">
        <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-3 sm:p-4 text-center">
          <p className="text-[#555] text-[10px] sm:text-xs uppercase tracking-wide mb-1">Nivel</p>
          <p className="text-white font-heading font-bold text-base sm:text-lg">
            {getLevelName(profile?.level) || "—"}
          </p>
          <p className="text-[#666] text-[10px] sm:text-xs mt-0.5 uppercase">
            {profile?.gender_display || (profile?.gender === "hombre" ? "HOMBRE" : profile?.gender === "mujer" ? "MUJER" : profile?.gender === "male" ? "HOMBRE" : profile?.gender === "female" ? "MUJER" : "")}
          </p>
        </div>
        <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-3 sm:p-4 text-center">
          <p className="text-[#555] text-[10px] sm:text-xs uppercase tracking-wide mb-1">Último entreno</p>
          <p className="text-white font-heading font-bold text-base sm:text-lg">
            {getLastWorkoutText(streak?.last_workout_date)}
          </p>
          {trainedToday && (
            <p className="text-[#22C55E] text-[10px] sm:text-xs mt-0.5 flex items-center justify-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Completado
            </p>
          )}
        </div>
      </div>

      {/* Today's Workout Card */}
      {workout && (
        <Card className={cn(
          "bg-gradient-to-br from-[#1A1A1A] to-[#121212] mb-4 sm:mb-5",
          isCustomWorkout 
            ? "border-[#D4AF37] border-2 shadow-lg shadow-[#D4AF37]/20" 
            : "border-[#D4AF37]/30"
        )}>
          <CardContent className="p-3 sm:p-5">
            {/* Custom Program Badge */}
            {isCustomWorkout && (
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#D4AF37]/30">
                <Star className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">
                  Mi Entrenamiento Personalizado
                </span>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="min-w-0 flex-1">
                <p className="text-[#D4AF37] text-[10px] sm:text-xs font-semibold uppercase tracking-widest mb-0.5 sm:mb-1">
                  {isCustomWorkout ? workout.program_name || "Rutina Personalizada" : "Hoy entrenas"}
                </p>
                <h2 className="text-xl sm:text-2xl font-heading font-black text-white truncate">
                  {getDayName(workout.code)}
                </h2>
              </div>
              <div className={cn(
                "w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 ml-2",
                isCustomWorkout 
                  ? "bg-gradient-to-br from-[#D4AF37] to-orange-500" 
                  : "bg-[#D4AF37]/20 border border-[#D4AF37]/40"
              )}>
                {isCustomWorkout ? (
                  <Star className="w-6 h-6 sm:w-7 sm:h-7 text-[#0B0B0B]" />
                ) : (
                  <span className="text-xl sm:text-2xl font-heading font-black text-[#D4AF37]">
                    {workout.code}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4 sm:gap-5 pt-2 sm:pt-3 border-t border-[#2A2A2A]">
              {!isCustomWorkout && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#666]" />
                  <span className="text-[#888] text-xs sm:text-sm">Semana {workout.week}</span>
                </div>
              )}
              {isCustomWorkout && workout.objective && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Target className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#666]" />
                  <span className="text-[#888] text-xs sm:text-sm capitalize">{workout.objective}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#666]" />
                <span className="text-[#888] text-xs sm:text-sm">{workout.exercises?.length || 0} ejercicios</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No workout state */}
      {!workout && (
        <Card className="bg-[#121212] border-[#2A2A2A] mb-5">
          <CardContent className="p-8 text-center">
            <Flame className="w-12 h-12 text-[#333] mx-auto mb-4" />
            <p className="text-[#666]">Sin programa asignado</p>
            <p className="text-[#555] text-sm mt-2">Solicítalo en recepción</p>
          </CardContent>
        </Card>
      )}

      {/* Primary Action - DIRECT TO WORKOUT MODE */}
      {workout && (
        <div className="space-y-3 mb-4 sm:mb-5">
          {/* Main CTA - Direct to WorkoutMode */}
          <Button
            onClick={onStartWorkout}
            disabled={ctaDisabled}
            className={cn(
              "w-full h-14 sm:h-16 font-black uppercase tracking-wider text-sm sm:text-lg rounded-xl shadow-lg transition-all active:scale-[0.98]",
              ctaDisabled 
                ? "bg-[#22C55E] text-white shadow-[#22C55E]/20 cursor-default"
                : streak?.current_streak >= 1
                  ? "bg-gradient-to-r from-[#D4AF37] to-orange-500 text-[#0B0B0B] shadow-orange-500/30 hover:shadow-orange-500/50"
                  : "bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] shadow-[#D4AF37]/30"
            )}
            data-testid="welcome-start-button"
          >
            {ctaDisabled ? (
              <>
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                {ctaText}
              </>
            ) : (
              <>
                EMPEZAR ENTRENAMIENTO
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2" />
              </>
            )}
          </Button>
          
          {/* Secondary CTA - View Details/Preview */}
          {!ctaDisabled && (
            <button
              onClick={onViewDetails}
              className="w-full py-3 text-[#D4AF37] text-sm font-medium hover:text-[#E5C54D] transition-colors flex items-center justify-center gap-2"
              data-testid="welcome-view-details-button"
            >
              <Info className="w-4 h-4" />
              Ver detalles del entrenamiento
            </button>
          )}
        </div>
      )}

      {/* Premium Banner - Only for non-premium users */}
      {!profile?.is_premium && (
        <PremiumBanner className="mb-4 sm:mb-5 mx-0" />
      )}

      {/* Secondary Options */}
      <div className="space-y-2 sm:space-y-3 mt-auto pb-4 sm:pb-6">
        <p className="text-[#555] text-[10px] sm:text-xs uppercase tracking-widest text-center mb-2 sm:mb-3">
          Más opciones
        </p>
        
        <button
          onClick={() => onNavigate("/programa")}
          className="w-full p-3 sm:p-4 rounded-xl bg-[#121212] border border-[#2A2A2A] hover:border-[#3A3A3A] transition-colors flex items-center gap-3 sm:gap-4"
          data-testid="welcome-programa-btn"
        >
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" />
          </div>
          <div className="text-left min-w-0 flex-1">
            <p className="text-white font-semibold text-sm sm:text-base">Programa completo</p>
            <p className="text-[#666] text-xs sm:text-sm">Ver las 8 semanas</p>
          </div>
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#555] flex-shrink-0" />
        </button>

        <button
          onClick={() => onNavigate("/historial")}
          className="w-full p-3 sm:p-4 rounded-xl bg-[#121212] border border-[#2A2A2A] hover:border-[#3A3A3A] transition-colors flex items-center gap-3 sm:gap-4"
          data-testid="welcome-historial-btn"
        >
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0">
            <History className="w-4 h-4 sm:w-5 sm:h-5 text-[#22C55E]" />
          </div>
          <div className="text-left min-w-0 flex-1">
            <p className="text-white font-semibold text-sm sm:text-base">Historial de entrenos</p>
            <p className="text-[#666] text-xs sm:text-sm">Tu progreso y racha</p>
          </div>
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#555] flex-shrink-0" />
        </button>

        <button
          onClick={() => toast.info("Próximamente: Consejos y técnica")}
          className="w-full p-3 sm:p-4 rounded-xl bg-[#121212] border border-[#2A2A2A] hover:border-[#3A3A3A] transition-colors flex items-center gap-3 sm:gap-4 opacity-60"
          data-testid="welcome-tips-btn"
        >
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
          </div>
          <div className="text-left min-w-0 flex-1">
            <p className="text-white font-semibold text-sm sm:text-base">Consejos y técnica</p>
            <p className="text-[#666] text-xs sm:text-sm">Próximamente</p>
          </div>
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#555] flex-shrink-0" />
        </button>
      </div>
    </div>
  );
};

// Main HoyPage Component
const HoyPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [workout, setWorkout] = useState(null);
  const [program, setProgram] = useState(null);
  const [profile, setProfile] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedExercises, setCompletedExercises] = useState([]);
  
  // Custom program state for premium users
  const [customWorkout, setCustomWorkout] = useState(null);
  const [hasCustomProgram, setHasCustomProgram] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationStreak, setCelebrationStreak] = useState(0);
  const [needsLevelSelection, setNeedsLevelSelection] = useState(false);
  const [motivationalLine] = useState(
    motivationalLines[Math.floor(Math.random() * motivationalLines.length)]
  );
  
  // Global timer hook
  const { startTimer, getDefaultRestTime } = useTimer();
  
  // Tracking modal state
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingExercise, setTrackingExercise] = useState(null);
  
  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailExercise, setDetailExercise] = useState(null);
  
  // Workout Mode state
  const [showWorkoutMode, setShowWorkoutMode] = useState(false);
  
  // Workout Preview state (new)
  const [showWorkoutPreview, setShowWorkoutPreview] = useState(false);
  
  // Animation state for exercise completion
  const [recentlyCompleted, setRecentlyCompleted] = useState(null);
  
  // Welcome flow states
  const [showSplash, setShowSplash] = useState(() => {
    const hasSeenSplash = sessionStorage.getItem("goatgym_splash_seen");
    return !hasSeenSplash;
  });
  // SIEMPRE mostrar el flujo moderno (Welcome → WorkoutMode)
  // Eliminado el flujo legacy con checkboxes y "MARCAR TODO COMPLETADO"
  const [showWelcome, setShowWelcome] = useState(true);
  const [workoutStarted, setWorkoutStarted] = useState(false);

  // New user onboarding state - check localStorage AND if user has no program yet
  const [showNewUserOnboarding, setShowNewUserOnboarding] = useState(() => {
    const hasCompletedOnboarding = localStorage.getItem("goatgym_onboarding");
    return !hasCompletedOnboarding;
  });
  
  // Track if onboarding just completed (to avoid showing level selector)
  const [onboardingJustCompleted, setOnboardingJustCompleted] = useState(false);
  
  // Force onboarding for new users who somehow bypassed it
  const [forceOnboarding, setForceOnboarding] = useState(false);

  // Handle onboarding completion - auto-select level from preferences
  const handleOnboardingComplete = async (preferences) => {
    setShowNewUserOnboarding(false);
    setForceOnboarding(false);
    setOnboardingJustCompleted(true);
    console.log("Onboarding completed with preferences:", preferences);
    
    // Auto-select level based on onboarding preferences
    if (preferences.level && preferences.track) {
      try {
        setLoading(true);
        const response = await axios.post(
          `${API_URL}/api/member/select-level`,
          { 
            level: preferences.level, 
            track: preferences.track
          },
          { headers: getAuthHeaders() }
        );
        
        console.log("Level auto-selected successfully:", response.data);
        
        // Set program immediately from response
        setProgram(response.data.program);
        setNeedsLevelSelection(false);
        
        // Fetch today's workout with the new program
        await fetchTodayWorkout();
      } catch (error) {
        console.error("Error auto-selecting level from onboarding:", error);
        // Only show level selector if onboarding didn't complete
        setOnboardingJustCompleted(false);
        setNeedsLevelSelection(true);
        setLoading(false);
      }
    } else {
      // No preferences, show level selector
      setOnboardingJustCompleted(false);
      setNeedsLevelSelection(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayWorkout();
  }, []);

  const fetchTodayWorkout = async () => {
    try {
      // First check profile to see if user has a level selected
      const profileRes = await axios.get(`${API_URL}/api/member/profile`, {
        headers: getAuthHeaders(),
      });
      setProfile(profileRes.data);
      
      // Set streak from profile
      setStreak({
        current_streak: profileRes.data.current_streak || 0,
        best_streak: profileRes.data.best_streak || 0,
        last_workout_date: profileRes.data.last_workout_date
      });
      
      // === PREMIUM: Check for custom workout first ===
      // Use user.is_premium from AuthContext (not profileRes which doesn't have this field)
      if (user?.is_premium) {
        try {
          const customRes = await axios.get(`${API_URL}/api/my-custom-workout/today`, {
            headers: getAuthHeaders(),
          });
          
          if (customRes.data.has_custom_workout && customRes.data.workout) {
            console.log("Premium user has active custom workout:", customRes.data.workout.program_name);
            setCustomWorkout(customRes.data.workout);
            setHasCustomProgram(true);
            
            // Transform custom workout to match generic workout format for compatibility
            const transformedWorkout = {
              id: customRes.data.workout.id,
              code: customRes.data.workout.day_name?.charAt(0) || "C",
              day_name: customRes.data.workout.day_name,
              week: 1,
              session_index: customRes.data.workout.day_number || 1,
              exercises: customRes.data.workout.exercises?.map(ex => ({
                id: ex.id,
                exercise_id: ex.exercise_id,
                name: ex.name || ex.exercise?.name || "Ejercicio",
                muscle_group: ex.muscle_group || ex.exercise?.muscle_group || "",
                sets: ex.sets,
                reps: ex.reps,
                rest_seconds: ex.rest_seconds,
                rir: ex.rir,
                technique_notes: ex.technique_notes,
                video_url: ex.video_url || ex.exercise?.video_url,
                exercise: ex.exercise,
                is_custom: true
              })) || [],
              completed_today: customRes.data.workout.is_completed,
              is_custom_program: true,
              program_name: customRes.data.workout.program_name,
              objective: customRes.data.workout.objective,
              location: customRes.data.workout.location
            };
            
            setWorkout(transformedWorkout);
            
            // User has custom program - disable onboarding
            setShowNewUserOnboarding(false);
            setForceOnboarding(false);
            
            if (customRes.data.workout.is_completed) {
              setShowCompletion(true);
              setShowWelcome(false);
            }
            
            setLoading(false);
            return; // Don't fetch generic workout
          }
        } catch (customErr) {
          console.log("No custom workout or error fetching:", customErr.message);
          // Continue to generic workout as fallback
        }
      }
      
      // Reset custom program state if no active custom workout
      setCustomWorkout(null);
      setHasCustomProgram(false);
      
      // If no program assigned, check if user should see onboarding
      if (!profileRes.data.program_id) {
        const onboardingData = localStorage.getItem("goatgym_onboarding");
        
        // If no onboarding data saved, this is a new user - show onboarding
        if (!onboardingData) {
          console.log("New user detected - showing onboarding");
          setForceOnboarding(true);
          setLoading(false);
          return;
        }
        
        // If we have onboarding data, try to auto-select the program
        try {
          const preferences = JSON.parse(onboardingData);
          if (preferences.level && preferences.track) {
            console.log("Auto-selecting from stored onboarding:", preferences);
            const response = await axios.post(
              `${API_URL}/api/member/select-level`,
              { 
                level: preferences.level, 
                track: preferences.track
              },
              { headers: getAuthHeaders() }
            );
            setProgram(response.data.program);
            setNeedsLevelSelection(false);
            // Refetch profile with new program
            const newProfileRes = await axios.get(`${API_URL}/api/member/profile`, {
              headers: getAuthHeaders(),
            });
            setProfile(newProfileRes.data);
          } else {
            // Onboarding data incomplete, show level selector
            setNeedsLevelSelection(true);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error("Error auto-selecting from stored onboarding:", e);
          setNeedsLevelSelection(true);
          setLoading(false);
          return;
        }
      } else {
        // User already has a program - NEVER show onboarding
        // This fixes the cache-clearing bug where localStorage gets wiped
        setShowNewUserOnboarding(false);
        setForceOnboarding(false);
      }

      const response = await axios.get(`${API_URL}/api/workout/today`, {
        headers: getAuthHeaders(),
      });
      setWorkout(response.data.workout);
      
      // Fetch program details for dynamic day names
      if (profileRes.data.program_id) {
        try {
          const progRes = await axios.get(`${API_URL}/api/programs/${profileRes.data.program_id}`, {
            headers: getAuthHeaders(),
          });
          setProgram(progRes.data);
        } catch (e) {
          // Program fetch is optional
        }
      }
      
      if (response.data.workout?.exercises_completed?.length > 0) {
        setCompletedExercises(response.data.workout.exercises_completed);
        setWorkoutStarted(true);
        setShowWelcome(false);
      }
      // Only show completion if completed_today AND there are completed exercises
      if (response.data.workout?.completed_today && response.data.workout?.exercises_completed?.length > 0) {
        setShowCompletion(true);
        setShowWelcome(false);
      }
    } catch (error) {
      console.error("Error fetching workout:", error);
      toast.error("Error al cargar el entrenamiento");
    } finally {
      setLoading(false);
    }
  };

  const handleLevelSelected = (data) => {
    setNeedsLevelSelection(false);
    setProgram(data.program);
    // Reset welcome screen to show after level selection
    setShowWelcome(true);
    sessionStorage.removeItem("goatgym_welcome_seen");
    // Refetch workout data
    setLoading(true);
    fetchTodayWorkout();
  };

  const handleChangeLevel = () => {
    // Force show level selector
    setNeedsLevelSelection(true);
    setShowWelcome(false);
    setWorkoutStarted(false);
  };

  const handleSplashComplete = () => {
    sessionStorage.setItem("goatgym_splash_seen", "true");
    setShowSplash(false);
  };

  const handleStartFromWelcome = () => {
    sessionStorage.setItem("goatgym_welcome_seen", "true");
    // Flujo RÁPIDO: Directo a WorkoutMode
    setShowWorkoutMode(true);
  };
  
  const handleViewDetailsFromWelcome = () => {
    sessionStorage.setItem("goatgym_welcome_seen", "true");
    // Flujo DETALLADO: Mostrar Preview primero
    setShowWorkoutPreview(true);
  };
  
  const handleStartWorkoutFromPreview = () => {
    // Cerrar preview y abrir WorkoutMode
    setShowWorkoutPreview(false);
    setShowWorkoutMode(true);
  };
  
  const handleBackFromPreview = () => {
    // Volver a la pantalla de bienvenida
    setShowWorkoutPreview(false);
  };

  const handleNavigate = (path) => {
    sessionStorage.setItem("goatgym_welcome_seen", "true");
    navigate(path);
  };

  const toggleExercise = async (exerciseId) => {
    const wasCompleted = completedExercises.includes(exerciseId);
    const newCompleted = wasCompleted
      ? completedExercises.filter((id) => id !== exerciseId)
      : [...completedExercises, exerciseId];
    
    setCompletedExercises(newCompleted);

    // Trigger animation when marking exercise as complete
    if (!wasCompleted) {
      setRecentlyCompleted(exerciseId);
      // Clear animation after it plays
      setTimeout(() => setRecentlyCompleted(null), 500);
    }

    // Show rest timer when marking exercise as complete (not when unchecking)
    if (!wasCompleted && workout) {
      const currentIndex = workout.exercises.findIndex(ex => ex.id === exerciseId);
      const currentExercise = workout.exercises[currentIndex];
      const nextExercise = workout.exercises[currentIndex + 1];
      
      // Only show timer if there's rest time and more exercises
      if (currentExercise?.rest_seconds > 0 && (nextExercise || currentIndex < workout.exercises.length - 1)) {
        startTimer(
          currentExercise.rest_seconds,
          currentExercise.name,
          nextExercise?.name || "próxima serie"
        );
      }
    }

    try {
      await axios.post(
        `${API_URL}/api/workout/complete`,
        {
          workout_day_id: workout.id,
          exercises_completed: newCompleted,
        },
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      console.error("Error saving progress:", error);
    }
  };

  const completeWorkout = async () => {
    if (!workout) return;
    
    setSaving(true);
    try {
      const allExerciseIds = workout.exercises.map((ex) => ex.id);
      const response = await axios.post(
        `${API_URL}/api/workout/complete`,
        {
          workout_day_id: workout.id,
          exercises_completed: allExerciseIds,
        },
        { headers: getAuthHeaders() }
      );
      
      setCompletedExercises(allExerciseIds);
      
      // Update streak from response
      const newStreak = response.data.current_streak || 1;
      setStreak({
        current_streak: newStreak,
        best_streak: response.data.best_streak || newStreak
      });
      
      // Show celebration if it's a new completion
      if (response.data.is_new_completion) {
        setCelebrationStreak(newStreak);
        setShowCelebration(true);
      }
      
      setShowCompletion(true);
    } catch (error) {
      console.error("Error completing workout:", error);
      toast.error("Error al completar");
    } finally {
      setSaving(false);
    }
  };

  // Show celebration overlay
  if (showCelebration) {
    return (
      <CelebrationOverlay 
        streak={celebrationStreak}
        onClose={() => setShowCelebration(false)}
      />
    );
  }

  // Show splash screen
  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  // Show loading
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#666] mt-4">Cargando...</p>
      </div>
    );
  }

  // Show new user onboarding (first time users)
  // Priority: onboarding > level selector
  // Show onboarding if: user hasn't completed it OR forceOnboarding is set
  // BUT: Skip onboarding if user already has a custom program (premium users with assigned routines)
  // Don't require profile - new users might not have one yet
  const shouldShowOnboarding = (showNewUserOnboarding || forceOnboarding) && !hasCustomProgram;
  
  if (shouldShowOnboarding && !loading) {
    console.log("🆕 Showing onboarding - new user detected");
    return (
      <OnboardingFlow 
        onComplete={handleOnboardingComplete}
      />
    );
  }

  // Show level selector if user needs to select a level (only if onboarding is done)
  if (needsLevelSelection) {
    console.log("📋 Showing level selector - user needs to select level");
    return <LevelSelectorPage onLevelSelected={handleLevelSelected} />;
  }

  // Check if user trained today - ONLY if there are exercises AND they've been explicitly completed
  const trainedToday = showCompletion || (
    workout?.exercises?.length > 0 && 
    completedExercises.length > 0 && 
    completedExercises.length === workout.exercises.length
  );

  // Show welcome screen - this is the main HOY view now
  // WorkoutPreview and WorkoutMode render via Portals so they overlay on top
  if (showWelcome && !workoutStarted) {
    return (
      <>
        <WelcomeScreen 
          workout={workout}
          program={program}
          profile={profile}
          streak={streak}
          trainedToday={trainedToday}
          onStartWorkout={handleStartFromWelcome}
          onViewDetails={handleViewDetailsFromWelcome}
          onNavigate={handleNavigate}
          onChangeLevel={handleChangeLevel}
          hasCustomProgram={hasCustomProgram}
        />
        
        {/* Workout Preview - Full Screen Portal (Read-only) */}
        {showWorkoutPreview && workout && (
          <WorkoutPreview
            workout={workout}
            program={program}
            onBack={handleBackFromPreview}
            onStartWorkout={handleStartWorkoutFromPreview}
          />
        )}
        
        {/* Workout Mode - Full Screen Portal (Active training) */}
        {showWorkoutMode && workout && (
          <WorkoutMode
            workout={workout}
            onClose={() => {
              setShowWorkoutMode(false);
            }}
            onComplete={() => {
              setShowWorkoutMode(false);
              setShowCompletion(true);
              // Mantener showWelcome=true para que al cerrar completion vuelva a Welcome
              // NO establecer workoutStarted=true para evitar pantalla antigua
              fetchTodayWorkout();
            }}
          />
        )}
      </>
    );
  }


  // FALLBACK: Si llegamos aquí con un workout, significa que por algún motivo
  // no se mostró el flujo moderno. Forzamos volver a WelcomeScreen.
  // Esto elimina completamente el flujo legacy con "MARCAR TODO COMPLETADO"
  return (
    <>
      <WelcomeScreen 
        workout={workout}
        program={program}
        profile={profile}
        streak={streak}
        trainedToday={showCompletion}
        onStartWorkout={handleStartFromWelcome}
        onViewDetails={handleViewDetailsFromWelcome}
        onNavigate={handleNavigate}
        onChangeLevel={handleChangeLevel}
        hasCustomProgram={hasCustomProgram}
      />
      
      {/* Workout Preview - Full Screen Portal (Read-only) */}
      {showWorkoutPreview && workout && (
        <WorkoutPreview
          workout={workout}
          program={program}
          onBack={handleBackFromPreview}
          onStartWorkout={handleStartWorkoutFromPreview}
        />
      )}
      
      {/* Workout Mode - Full Screen Portal (Active training) */}
      {showWorkoutMode && workout && (
        <WorkoutMode
          workout={workout}
          onClose={() => {
            setShowWorkoutMode(false);
          }}
          onComplete={() => {
            setShowWorkoutMode(false);
            setShowCompletion(true);
            fetchTodayWorkout();
          }}
        />
      )}
    </>
  );
};

export default HoyPage;
